import { useEffect, useId, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { IpCell } from "@/components/IpCell";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { Sparkline } from "@/components/Sparkline";
import type { ProjectDetail as ProjectDetailT } from "@/types";

const REFRESH_MS = 10_000;

function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const sec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function fmtCost(usd: number, decimals = 4): string {
  if (usd === 0) return "$0";
  return `$${usd.toFixed(decimals)}`;
}

function fmtBytes(b: number): string {
  if (b === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function statusTone(s: number | null): string {
  if (s === null) return "text-slate-400 bg-white/5 border-white/10";
  if (s >= 500) return "text-rose-300 bg-rose-500/10 border-rose-500/30";
  if (s >= 400) return "text-amber-300 bg-amber-500/10 border-amber-500/30";
  if (s >= 300) return "text-sky-300 bg-sky-500/10 border-sky-500/30";
  return "text-emerald-300 bg-emerald-500/10 border-emerald-500/30";
}

export function ProjectDetail() {
  const { name } = useParams();
  const [detail, setDetail] = useState<ProjectDetailT | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const fetchNow = async () => {
      if (!name) return;
      try {
        const d = await api<ProjectDetailT>(
          `/api/admin/projects/${encodeURIComponent(name)}`
        );
        if (!cancelled) {
          setDetail(d);
          setUpdatedAt(Date.now());
          setErr(null);
        }
      } catch (e: unknown) {
        if (!cancelled) setErr((e as Error)?.message ?? "load failed");
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === "visible") await fetchNow();
        if (!cancelled) schedule();
      }, REFRESH_MS);
    };

    fetchNow();
    schedule();
    const onVis = () => {
      if (document.visibilityState === "visible") fetchNow();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [name]);

  const liveBadge = useMemo(() => {
    if (!detail?.summary.last_seen) return false;
    return Date.now() - new Date(detail.summary.last_seen).getTime() < 5 * 60_000;
  }, [detail?.summary.last_seen]);

  if (!detail && !err) {
    return <div className="card text-slate-500 text-sm">Loading…</div>;
  }
  if (!detail && err) {
    const authExpired = err.toLowerCase().includes("authenticat");
    return (
      <div className="card text-rose-300 text-sm">
        {err}
        {authExpired ? (
          <Link to="/login" className="ml-3 underline text-accent-500">
            re-login
          </Link>
        ) : (
          <Link to="/projects" className="ml-3 underline text-accent-500">
            Back to projects
          </Link>
        )}
      </div>
    );
  }
  if (!detail) return null;

  const s = detail.summary;
  const errPct = s.error_rate_24h * 100;
  const ago = updatedAt === null ? null : Math.max(0, Math.round((Date.now() - updatedAt) / 1000));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/projects" className="hover:text-slate-200">
          Projects
        </Link>
        <span>/</span>
        <span className={`text-slate-300 ${detail.is_unset ? "italic" : ""}`}>
          {detail.project}
        </span>
      </div>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className={`text-4xl display tracking-tight ${detail.is_unset ? "text-slate-500 italic" : ""}`}>
            {detail.project}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {s.real_key_count} real key{s.real_key_count === 1 ? "" : "s"} · {s.key_count} proxy
            key{s.key_count === 1 ? "" : "s"} ({s.active_key_count} active
            {s.revoked_key_count > 0 ? `, ${s.revoked_key_count} revoked` : ""}
            {s.expired_key_count > 0 ? `, ${s.expired_key_count} expired` : ""}). First seen{" "}
            {relTime(s.first_seen)}.
          </p>
        </div>
        <div className="flex items-center gap-3 text-right">
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
              liveBadge
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                : "bg-white/5 text-slate-500 border border-white/10"
            }`}
          >
            {liveBadge ? "● live" : "idle"}
          </span>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                ago !== null && ago < 12 ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
              }`}
            />
            {ago === null ? "loading…" : ago < 5 ? "fresh" : `${ago}s ago`}
          </div>
        </div>
      </header>

      {err && (
        <div className="card text-rose-300 text-sm">
          {err}{" "}
          {err.toLowerCase().includes("authenticat") ? (
            <Link to="/login" className="ml-1 underline text-accent-500">
              re-login
            </Link>
          ) : (
            <span className="text-slate-500">(showing last successful snapshot)</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Requests · 24h" value={s.requests_24h.toLocaleString()} />
        <Stat
          label="Errors · 24h"
          value={`${s.errors_24h}${errPct > 0 ? ` · ${errPct.toFixed(1)}%` : ""}`}
          tone={errPct > 5 ? "rose" : errPct > 0 ? "amber" : "neutral"}
        />
        <Stat
          label="Avg latency"
          value={s.avg_latency_ms > 0 ? `${Math.round(s.avg_latency_ms)} ms` : "—"}
        />
        <Stat
          label="Cost · 24h"
          value={s.cost_24h_usd > 0 ? fmtCost(s.cost_24h_usd, 4) : "—"}
          help="Sum across ALL keys for this project — including revoked or expired ones (historical traffic still counts)."
        />
        <Stat
          label="Cost · MTD"
          value={s.cost_mtd_usd > 0 ? fmtCost(s.cost_mtd_usd, 2) : "—"}
          help="Month-to-date cost across all keys (incl. revoked/expired)."
        />
        <Stat label="Last seen" value={relTime(s.last_seen)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-sm">7-day traffic</h2>
            <span className="text-xs text-slate-500">
              {detail.daily_series.reduce((sum, p) => sum + p.requests, 0)} req,{" "}
              {detail.daily_series.reduce((sum, p) => sum + p.errors, 0)} err
            </span>
          </div>
          <Sparkline data={detail.daily_series} width={520} height={84} />
        </section>
        <section className="card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-sm">Last 24 hours</h2>
            <span className="text-xs text-slate-500">
              {fmtBytes(s.bytes_down_24h)} down · {detail.hourly_series.length} buckets
            </span>
          </div>
          <HourlySpark data={detail.hourly_series} live={liveBadge} />
        </section>
      </div>

      <section className="card">
        <h2 className="font-semibold mb-3">Real keys (upstream)</h2>
        {detail.real_keys.length === 0 ? (
          <div className="text-sm text-slate-500">No real keys linked to this project.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {detail.real_keys.map((rk) => {
              const usedPct =
                rk.quota_amount && rk.quota_used !== null
                  ? Math.min(100, (rk.quota_used / rk.quota_amount) * 100)
                  : null;
              return (
                <div
                  key={rk.id}
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Link
                      to={`/real-keys/${rk.id}`}
                      className="flex items-center gap-2 min-w-0 hover:text-accent-500"
                    >
                      <ProviderGlyph provider={rk.provider} size={32} />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-100 truncate">{rk.label}</div>
                        <div className="text-xs text-slate-500 mono truncate">{rk.provider}</div>
                      </div>
                    </Link>
                    {rk.quota_exhausted && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">
                        exhausted
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-400 mono">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Rotated</div>
                      <div>
                        {relTime(rk.rotated_at)}
                        {rk.rotation_age_days !== null && rk.rotation_age_days > 90 && (
                          <span className="ml-1 text-amber-300">(stale)</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        Monthly budget
                      </div>
                      <div>
                        {rk.monthly_budget_usd !== null
                          ? `$${rk.monthly_budget_usd.toFixed(2)}`
                          : "none"}
                      </div>
                    </div>
                    {usedPct !== null && (
                      <div className="col-span-2">
                        <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500">
                          <span>
                            Quota {rk.quota_unit ?? ""} {rk.quota_period ?? ""}
                          </span>
                          <span>{usedPct.toFixed(0)}%</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className={`h-full ${
                              usedPct > 90
                                ? "bg-rose-400"
                                : usedPct > 70
                                ? "bg-amber-400"
                                : "bg-emerald-400"
                            }`}
                            style={{ width: `${usedPct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mono truncate" title={rk.upstream_base_url}>
                    {rk.upstream_base_url}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold mb-3">
          Proxy keys{" "}
          <span className="text-xs text-slate-500 font-normal">
            ({detail.proxy_keys.length})
          </span>
        </h2>
        {detail.proxy_keys.length === 0 ? (
          <div className="text-sm text-slate-500">No proxy keys.</div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-2">Label</th>
                  <th className="text-left py-2 px-2">Real key</th>
                  <th className="text-right py-2 px-2">24h req</th>
                  <th className="text-right py-2 px-2">errors</th>
                  <th className="text-left py-2 px-2">Last seen</th>
                  <th className="text-left py-2 px-2">Expires</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {detail.proxy_keys.map((pk) => {
                  const expired = pk.expires_at && new Date(pk.expires_at) < new Date();
                  return (
                    <tr key={pk.id} className="hover:bg-white/[0.02]">
                      <td className="py-2 px-2 min-w-0">
                        <Link
                          to={`/proxy-keys/${pk.id}`}
                          className="font-medium text-slate-200 hover:text-accent-500"
                        >
                          {pk.label}
                        </Link>
                        <div className="text-[10px] text-slate-500 mono truncate">{pk.prefix}…</div>
                      </td>
                      <td className="py-2 px-2">
                        {pk.real_key_provider && pk.real_key_label ? (
                          <Link
                            to={`/real-keys/${pk.real_key_id}`}
                            className="inline-flex items-center gap-1.5 hover:text-accent-500"
                          >
                            <ProviderGlyph provider={pk.real_key_provider} size={18} />
                            <span className="truncate max-w-[140px]">{pk.real_key_label}</span>
                          </Link>
                        ) : (
                          <span className="text-slate-500">#{pk.real_key_id}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right mono">
                        {pk.requests_24h.toLocaleString()}
                      </td>
                      <td className="py-2 px-2 text-right mono">
                        {pk.errors_24h > 0 ? (
                          <span className="text-rose-300">{pk.errors_24h}</span>
                        ) : (
                          <span className="text-slate-500">0</span>
                        )}
                      </td>
                      <td className="py-2 px-2 mono text-slate-400">{relTime(pk.last_seen)}</td>
                      <td className="py-2 px-2 mono text-slate-400">
                        {pk.expires_at ? fmtTs(pk.expires_at) : "never"}
                      </td>
                      <td className="py-2 px-2">
                        {pk.revoked_at ? (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">
                            revoked
                          </span>
                        ) : expired ? (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                            expired
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card">
          <h2 className="font-semibold mb-3">Top paths · 24h</h2>
          {detail.top_paths.length === 0 ? (
            <div className="text-sm text-slate-500">No traffic in the last 24 hours.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1">Path</th>
                  <th className="text-right py-1">Requests</th>
                  <th className="text-right py-1">Errors</th>
                  <th className="text-right py-1">Avg ms</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {detail.top_paths.map((p) => (
                  <tr key={p.path}>
                    <td className="py-1.5 mono text-slate-300 truncate max-w-[260px]" title={p.path}>
                      {p.path}
                    </td>
                    <td className="py-1.5 mono text-right text-slate-200">
                      {p.requests.toLocaleString()}
                    </td>
                    <td
                      className={`py-1.5 mono text-right ${
                        p.errors > 0 ? "text-rose-300" : "text-slate-500"
                      }`}
                    >
                      {p.errors}
                    </td>
                    <td className="py-1.5 mono text-right text-slate-400">
                      {Math.round(p.avg_latency_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card">
          <h2 className="font-semibold mb-3">Recent errors · 24h</h2>
          {detail.recent_errors.length === 0 ? (
            <div className="text-sm text-emerald-300/80">No errors in the last 24 hours. ✓</div>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {detail.recent_errors.map((e) => (
                <li key={e.id} className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] mono text-slate-500 shrink-0">
                    {e.ts ? new Date(e.ts).toLocaleTimeString() : "?"}
                  </span>
                  <span
                    className={`text-[10px] mono px-1.5 py-0.5 rounded border ${statusTone(
                      e.upstream_status
                    )}`}
                  >
                    {e.upstream_status ?? "ERR"}
                  </span>
                  <span className="text-[10px] mono text-slate-400 shrink-0">{e.method}</span>
                  <span className="mono text-slate-300 truncate" title={e.path}>
                    {e.path}
                  </span>
                  <Link
                    to={`/proxy-keys/${e.proxy_key_id}`}
                    className="text-[10px] text-slate-500 hover:text-accent-500 truncate ml-auto"
                  >
                    {e.proxy_key_label ?? "—"}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card">
        <h2 className="font-semibold mb-3">
          Recent activity{" "}
          <span className="text-xs text-slate-500 font-normal">
            (last {detail.recent_requests.length})
          </span>
        </h2>
        {detail.recent_requests.length === 0 ? (
          <div className="text-sm text-slate-500">No requests yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-1 px-2">Time</th>
                  <th className="text-left py-1 px-2">Status</th>
                  <th className="text-left py-1 px-2">Method</th>
                  <th className="text-left py-1 px-2">Path</th>
                  <th className="text-right py-1 px-2">ms</th>
                  <th className="text-right py-1 px-2">Down</th>
                  <th className="text-right py-1 px-2">Cost</th>
                  <th className="text-left py-1 px-2">Source</th>
                  <th className="text-left py-1 px-2">Key</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {detail.recent_requests.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02]">
                    <td className="py-1 px-2 mono text-slate-400 whitespace-nowrap">
                      {r.ts ? new Date(r.ts).toLocaleTimeString() : "—"}
                    </td>
                    <td className="py-1 px-2">
                      <span
                        className={`text-[10px] mono px-1.5 py-0.5 rounded border ${statusTone(
                          r.upstream_status
                        )}`}
                      >
                        {r.upstream_status ?? "ERR"}
                      </span>
                    </td>
                    <td className="py-1 px-2 mono text-slate-400">{r.method}</td>
                    <td className="py-1 px-2 mono text-slate-300 truncate max-w-[280px]" title={r.path}>
                      {r.path}
                    </td>
                    <td className="py-1 px-2 mono text-right text-slate-400">
                      {r.latency_ms ?? "—"}
                    </td>
                    <td className="py-1 px-2 mono text-right text-slate-500">
                      {r.bytes_down !== null ? fmtBytes(r.bytes_down) : "—"}
                    </td>
                    <td className="py-1 px-2 mono text-right text-slate-500">
                      {r.estimated_cost_usd !== null ? `$${r.estimated_cost_usd.toFixed(5)}` : "—"}
                    </td>
                    <td className="py-1 px-2"><IpCell ip={r.source_ip} /></td>
                    <td className="py-1 px-2 mono text-slate-500 truncate max-w-[120px]">
                      {r.proxy_key_id ? (
                        <Link
                          to={`/proxy-keys/${r.proxy_key_id}`}
                          className="hover:text-accent-500"
                        >
                          {r.proxy_key_label ?? `#${r.proxy_key_id}`}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold mb-3">
          Audit log{" "}
          <span className="text-xs text-slate-500 font-normal">
            (last {detail.audit.length})
          </span>
        </h2>
        {detail.audit.length === 0 ? (
          <div className="text-sm text-slate-500">No audit events recorded for this project's keys yet.</div>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {detail.audit.map((a) => (
              <li key={a.id} className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] mono text-slate-500 shrink-0 w-32">
                  {fmtTs(a.ts)}
                </span>
                <span className="text-[10px] mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300 shrink-0">
                  {a.action}
                </span>
                <span className="text-slate-400 shrink-0">
                  {a.subject_type}#{a.subject_id ?? "—"}
                </span>
                <span className="text-slate-500 truncate ml-auto">
                  {a.actor_email ?? `actor#${a.actor_id ?? "—"}`}
                </span>
                {a.ip && (
                  <span className="hidden sm:inline shrink-0">
                    <IpCell ip={a.ip} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
  help,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "rose" | "amber";
  help?: string;
}) {
  const cls =
    tone === "rose"
      ? "text-rose-200 border-rose-500/30 bg-rose-500/[0.06]"
      : tone === "amber"
      ? "text-amber-200 border-amber-500/30 bg-amber-500/[0.06]"
      : "border-white/10 bg-white/[0.02]";
  // Sighted users get the native browser tooltip on hover via `title=` on
  // the ⓘ glyph; screen readers + keyboard users reach the same text via
  // aria-describedby pointing at a visually-hidden span. Without this the
  // help string is unreachable on touch and SR (audit pass 2, finding #2).
  const helpId = useId();
  return (
    <div
      className={`rounded-xl border p-4 ${cls}`}
      aria-describedby={help ? helpId : undefined}
    >
      {help && (
        <span id={helpId} className="sr-only">
          {help}
        </span>
      )}
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {label}
        {help && (
          <span
            className="text-slate-600 cursor-help"
            tabIndex={0}
            role="img"
            aria-label="more info"
            title={help}
          >
            ⓘ
          </span>
        )}
      </div>
      <div className="text-2xl mono font-semibold mt-1 truncate">{value}</div>
    </div>
  );
}

function HourlySpark({
  data,
  live,
}: {
  data: { ts: string; hour: string; requests: number; errors: number }[];
  live: boolean;
}) {
  const gradientId = `hourly-${useId().replace(/:/g, "")}`;
  if (!data || data.length === 0) {
    return <div className="h-20 rounded bg-white/[0.02] border border-white/5" />;
  }
  const W = 520;
  const H = 84;
  const pad = 4;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const max = Math.max(1, ...data.map((d) => d.requests));
  const stepX = innerW / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => ({
    x: pad + i * stepX,
    y: pad + innerH - (d.requests / max) * innerH,
    ...d,
  }));
  const lineD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${lineD} L${pad + innerW},${pad + innerH} L${pad},${pad + innerH} Z`;
  const stroke = live ? "rgb(110 231 183)" : "rgb(148 163 184)";
  const fillTop = live ? "rgba(16,185,129,0.32)" : "rgba(148,163,184,0.18)";
  const fillBot = live ? "rgba(16,185,129,0)" : "rgba(148,163,184,0)";
  // Vertical lines every 6h so the eye can read time segments at a glance.
  const gridXs = [6, 12, 18].map((i) => pad + i * stepX);

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="none"
        className="block"
        role="img"
        aria-label="24-hour request sparkline"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillTop} />
            <stop offset="100%" stopColor={fillBot} />
          </linearGradient>
        </defs>
        {gridXs.map((x) => (
          <line key={x} x1={x} y1={pad} x2={x} y2={pad + innerH} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        ))}
        <path d={areaD} fill={`url(#${gradientId})`} />
        <path d={lineD} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" />
        {pts.map((p) => (
          <g key={p.ts}>
            {p.errors > 0 && (
              <circle cx={p.x} cy={p.y} r={3} fill="none" stroke="rgb(244 63 94)" strokeWidth={1.1} opacity={0.85} />
            )}
            <title>
              {p.hour}: {p.requests} req{p.errors > 0 ? `, ${p.errors} err` : ""}
            </title>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 mono mt-1">
        <span>{data[0].hour}</span>
        <span>{data[data.length - 1].hour}</span>
      </div>
    </div>
  );
}

